import { CopyableText } from "@via/components/copyable-text/copyable-text.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import {
  BannerSize,
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner.tsx";
import {
  type AgentStatus,
  type IssuedAgent,
  type ProbeResult,
  useAttachAgent,
  useProbeAgent,
  useReissueAgentCredential,
} from "@via/pages/account/profile/run-environments/agent.api.tsx";
import { InstructionStep } from "@via/pages/account/profile/run-environments/instruction-step.tsx";
import { getDocsUrl } from "@via/utils/config.tsx";
import { AGENT_LIMITATIONS_DOCS_PATH } from "@via/utils/constants.tsx";
import { agentCapabilityGaps, agentInstanceNameFor } from "common";
import { type JSX, useState } from "react";

/**
 * Collapsed-card summary, read from the same status the card itself polls. `status` is
 * undefined until the first poll lands, which reads the same as "not attached yet" either way,
 * so both are folded into one line.
 */
export const agentCardSummary = (status?: AgentStatus): string => {
  if (!status?.attached) {
    return status && !status.hubEnabled
      ? "Agent connections disabled"
      : "Not attached";
  }
  return status.connected ? "Connected" : "Attached, not connected";
};

/**
 * A labeled value with the copy button already used for the personal access token and the
 * command on the Connect AI Assistants tab (CopyableText), rather than a second clipboard
 * affordance built here. Task 9 adds a probe result panel to this same card, so this stays a
 * small, separately named component the rest of the card can keep reusing.
 */
function CopyableCommand({
  label,
  value,
}: {
  label?: string;
  value: string;
}): JSX.Element {
  return (
    <div>
      {undefined === label ? null : (
        <p className="mb-1 text-xs font-medium text-gray-700">{label}</p>
      )}
      <CopyableText value={value} className="[&>span]:max-w-full" />
    </div>
  );
}

function AgentStatusLine({ status }: { status: AgentStatus }): JSX.Element {
  return (
    <div className="space-y-1">
      <p className="text-sm font-medium">
        {status.connected ? (
          <span className="text-emerald-600">Connected</span>
        ) : (
          <span className="text-amber-600">Attached, waiting to connect</span>
        )}
      </p>
      <p className="text-xs text-gray-500">
        Fingerprint {status.fingerprint}
        {status.hostname ? `, last seen on ${status.hostname}` : ""}. Lost the
        credential itself? Reissue below replaces it.
      </p>
      {status.connected && false === status.supervised ? (
        <div className="space-y-1">
          <p className="text-xs text-amber-700">
            Automatic recovery not verified on this login node. If the agent
            stops, nothing will restart it. Run this there:
          </p>
          <CopyableCommand label="" value="foundry-agent supervise" />
        </div>
      ) : null}
    </div>
  );
}

/**
 * agent-4-2 (spec 3G-3b A2). What the CONNECTED agent cannot do on this environment, one line per gap, in
 * the order and the words of `agentCapabilityGaps` in common. Nothing while no agent is connected
 * (`capabilities` is null), so an offline or unattached agent keeps the status line alone, and nothing
 * when there is no gap.
 */
export function AgentCapabilityWarning({
  status,
  hpcAppsEnabled,
}: {
  status: AgentStatus;
  hpcAppsEnabled: boolean;
}): JSX.Element | null {
  const gaps = agentCapabilityGaps(status.capabilities, { hpcAppsEnabled });
  if (0 === gaps.length) return null;
  return (
    <div data-testid="agent-capability-warning">
      <MessageBanner
        type={BannerType.Warning}
        size={BannerSize.Small}
        title="What this agent cannot do yet"
        messages={gaps.map(gap => `${gap.stops} ${gap.remedy}`)}
        footer={
          <a
            href={getDocsUrl(AGENT_LIMITATIONS_DOCS_PATH)}
            target="_blank"
            rel="noreferrer"
            className="underline">
            What agent environments do differently
          </a>
        }
      />
    </div>
  );
}

/**
 * The created state, shaped after science-ui's AgentSetupInstructions: a success panel that
 * lays the install out as numbered terminal steps rather than a paragraph.
 *
 * ⛔⭐ The three commands are deliberately NOT science-ui's. Its step 2 is
 * `configure --server X --token Y`, which puts the credential in argv where `ps` shows it to
 * every other user on a shared login node. Ours ends at `--server`; the value is pasted at a
 * prompt, because the agent reads it from stdin. Spec decision 6. A test walks every rendered
 * `code` block asserting none contains the plaintext.
 *
 * Exported for that test: driving attach through a mocked mutation just to reach this panel
 * would assert against the mock rather than the panel.
 */
export function IssuedPanel({ issued }: { issued: IssuedAgent }): JSX.Element {
  // The card cannot know which branch the installer took on the cluster, so it shows the usual
  // `foundry-agent` and names the other form for an account whose agent already serves another
  // Foundry Connect server. No name (an address the installer refuses) means no second form.
  const instance = agentInstanceNameFor(issued.serverUrl);
  return (
    <div className="space-y-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
      <p className="text-sm font-semibold text-emerald-800">
        Agent attached. Install it on your login node.
      </p>
      <p className="text-xs text-emerald-700">
        No administrator access is needed. Everything lives under your home
        directory.
      </p>

      <InstructionStep
        step={1}
        title="Install the agent"
        description="Run this on the login node where the agent will live. It asks for the credential below. The installer needs Python 3.8 or newer on PATH; on a cluster, load one first (for example: module load python3/3.8.2)."
        command={issued.installCommand}
      />
      <InstructionStep
        step={2}
        title="Configure it, only if you skipped that prompt"
        description="The installer already does this when you paste the credential where it asks."
        command={`foundry-agent configure --server ${issued.serverUrl}`}
      />
      {/* The export is part of the command, not decoration: the installer edits the shell
          profile, but the shell the user is sitting in was started before that edit, so a bare
          `foundry-agent start -b` fails there with command not found. New logins need only the
          second half; the export is harmless when repeated. */}
      <InstructionStep
        step={3}
        title="Start the agent"
        description="Runs in the background and dials back here. The export covers this shell; new logins will not need it."
        command={
          'export PATH="$HOME/.foundry/bin:$PATH"; foundry-agent start -b'
        }
      />
      {instance ? (
        <div className="space-y-1">
          <p className="text-xs text-gray-500">
            If the agent on this account already serves another Foundry Connect
            server, the installer names this one foundry-agent-{instance}{" "}
            instead. Use that name in steps 2 and 3:
          </p>
          <CopyableCommand
            value={`foundry-agent-${instance} configure --server ${issued.serverUrl}`}
          />
          <CopyableCommand
            value={`export PATH="$HOME/.foundry/bin:$PATH"; foundry-agent-${instance} start -b`}
          />
        </div>
      ) : null}

      <MessageBanner
        type={BannerType.Warning}
        title="Copy this now"
        messages="This is the only time you will see this credential. It will not be shown again, so copy it before doing anything else."
      />
      <CopyableCommand label="Credential" value={issued.credential} />
      <p className="text-xs text-gray-600">
        Fingerprint {issued.fingerprint}. It is safe to keep and is how support
        identifies this credential later, even after the credential itself is
        gone from the screen.
      </p>
    </div>
  );
}

/**
 * The four fields that decide whether a login node can run anything at all, on the face of the
 * card. Everything else the agent reported (`path`, `umask`, `locale`, `ulimits`) goes behind the
 * details toggle below: it is long, carries raw environment values, and is only wanted once one
 * of the fields above already looks wrong.
 *
 * ⚠️ `bashAvailable` and `moduleAvailable` sit next to hostname and the versions rather than in
 * the fold, because neither is guaranteed on an HPC login node, and a scientist reading this card
 * needs to know that before anything else.
 */
export function ProbeResultPanel({
  result,
  durationMs,
}: {
  result: ProbeResult;
  durationMs: number;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
      {/*
        The round trip time leads, as it does in science-ui. It is the one number that says
        whether the link is healthy rather than merely open, and a scientist reads it before
        any of the fields below.
      */}
      <p className="mb-2 text-sm font-medium text-emerald-700">
        Connection successful ({durationMs} ms)
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1">
        <dt className="text-gray-500">Host</dt>
        <dd>{result.hostname}</dd>
        <dt className="text-gray-500">Agent</dt>
        <dd>{result.agentVersion}</dd>
        <dt className="text-gray-500">Python</dt>
        <dd>{result.pythonVersion}</dd>
        {/*
          Rendered as words either way, never as an empty cell. science-ui does the same, and
          the reason matters on an HPC login node: "we checked and it is missing" and "we did
          not check" are different facts, and a blank row cannot tell them apart.
        */}
        <dt className="text-gray-500">bash</dt>
        <dd>{result.bashAvailable ? "Available" : "Not available"}</dd>
        <dt className="text-gray-500">module</dt>
        <dd>{result.moduleAvailable ? "Available" : "Not available"}</dd>
      </dl>
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-gray-500">
          Everything the agent reported
        </summary>
        <pre className="mt-1 overflow-x-auto text-xs">
          {JSON.stringify(result, null, 2)}
        </pre>
      </details>
    </div>
  );
}

/**
 * Turns a 409's machine `reason` into words a scientist can act on. Never surfaces `reason`
 * itself: a string like `not_connected` on its own tells them nothing about what to do next. The
 * reasons handled here are `DispatchFailureReason` (backend/src/business/run/transport/agent/
 * dispatch.ts) plus the hub-off case, which reuses `not_connected` from agentProbeHandler
 * (backend/src/controllers/agent.ts) since a caller cannot tell the two apart and should not need
 * to: starting the agent is the fix either way.
 */
function probeFailureMessage(error: unknown): string {
  const data = (
    error as { response?: { data?: { reason?: string; detail?: string } } }
  )?.response?.data;
  switch (data?.reason) {
    case "not_connected":
      return "Your agent on this environment is not connected right now. Starting it on the login node will fix that.";
    case "timeout":
      return "Your agent did not answer in time. It may be busy or unreachable right now. Try again in a moment.";
    case "capacity":
      return "Foundry Connect is at capacity for agent operations right now. Try again in a moment.";
    case "shutting_down":
      return "Foundry Connect is not accepting agent operations right now. Try again shortly.";
    case "not_journaled":
      return "The test connection request could not be recorded, so it was never sent. Try again.";
    case "unknown_method":
      return "Your agent does not support Test Connection yet. Update it and try again.";
    case "rejected":
      return data?.detail
        ? `Your agent rejected the test connection: ${data.detail}`
        : "Your agent rejected the test connection.";
    default:
      return "The test connection request did not go through. Check your connection and try again.";
  }
}

/**
 * Test Connection: the one call an operator makes to see what a login node actually looks like
 * from the agent's point of view, without SSH.
 *
 * ⚠️ `connected` only disables the button as a hint. It is not the guarantee: the status poll
 * backing it can be a few seconds stale, so the backend refuses on its own even when this reads
 * true (agentProbeHandler, backend/src/controllers/agent.ts), and that refusal is rendered the
 * same way below regardless of which path produced it.
 */
function TestConnectionSection({
  environmentId,
  connected,
}: {
  environmentId: number;
  connected: boolean;
}): JSX.Element {
  const probe = useProbeAgent(environmentId);

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => probe.mutate()}
        disabled={!connected || probe.isPending}
        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50">
        <Spinner loading={probe.isPending}>Test connection</Spinner>
      </button>
      {probe.isSuccess ? (
        <ProbeResultPanel
          result={probe.data.result}
          durationMs={probe.data.durationMs}
        />
      ) : null}
      {probe.isError ? (
        <MessageBanner
          type={BannerType.Error}
          messages={probeFailureMessage(probe.error)}
        />
      ) : null}
    </div>
  );
}

/**
 * ⚠️ `issued` is component state and nothing else. Attach and reissue return the plaintext
 * credential exactly once, and no other call, including status, ever returns it again. It must
 * never reach the query cache, `localStorage`, a URL, or a log: this state, gone the moment the
 * card unmounts, is the only place it is allowed to live. `status.fingerprint` is derived from a
 * hash rather than the credential itself, so it is safe to show forever and is what the card
 * falls back to once the plaintext is gone from the screen.
 */
export function AgentCard({
  environmentId,
  status,
  statusError = false,
  hpcAppsEnabled = false,
}: {
  environmentId?: number;
  /**
   * Polled by the form, not here. The form's collapsed summary for this card reads the same
   * value, so one subscription up there is what keeps the summary and the open card from
   * disagreeing, and it is also the only place that knows whether the id belongs to a saved
   * environment or to a public template still being tried on.
   */
  status?: AgentStatus;
  statusError?: boolean;
  /** The instance's HPC_APPS_ENABLED. The apps line of the capability warning shows only when it is on. */
  hpcAppsEnabled?: boolean;
}): JSX.Element {
  const [issued, setIssued] = useState<IssuedAgent | null>(null);
  const attach = useAttachAgent(environmentId);
  const reissue = useReissueAgentCredential(environmentId);

  if (!environmentId) {
    return (
      <p className="text-sm text-gray-500">
        Save this run environment first. Your agent attaches to it by id, so it
        has to exist before you can install one.
      </p>
    );
  }

  // ⚠️ Checked before anything that reads `status`, and before offering Attach. Without this,
  // a failed read renders exactly like "no agent yet": the card would invite attaching a second
  // agent over one that is already connected, or leave a scientist believing nothing is set up
  // when the truth is simply unknown right now.
  if (statusError) {
    return (
      <p className="text-sm text-rose-600">
        Could not check whether an agent is attached to this run environment.
        This is a problem with the check itself, not proof that none exists: if
        one is already attached and connected, reloading this page is safe and
        will not create a second one. Try again in a moment.
      </p>
    );
  }

  if (status && !status.hubEnabled) {
    return (
      <p className="text-sm text-gray-500">
        Agent connections are switched off for this deployment, so an agent
        installed here could never connect. Ask an administrator to turn them on
        before attaching one.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {status?.attached ? (
        <div className="space-y-3">
          <AgentStatusLine status={status} />
          <AgentCapabilityWarning
            status={status}
            hpcAppsEnabled={hpcAppsEnabled}
          />
          <TestConnectionSection
            environmentId={environmentId}
            connected={status.connected}
          />
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-gray-500">
            Attaching creates a credential for your agent and a command to run
            on the login node where it will live. Copy both before you close
            this drawer: the credential is shown only this once, and reopening
            the environment will not bring it back. Collapsing this card is
            safe, it stays here until you leave.
          </p>
          <button
            type="button"
            onClick={() => attach.mutate(undefined, { onSuccess: setIssued })}
            disabled={attach.isPending}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
            <Spinner loading={attach.isPending}>Attach my agent</Spinner>
          </button>
        </div>
      )}

      {issued ? <IssuedPanel issued={issued} /> : null}

      {status?.attached && !issued ? (
        <button
          type="button"
          onClick={() => reissue.mutate(undefined, { onSuccess: setIssued })}
          disabled={reissue.isPending}
          className="text-sm font-medium text-indigo-600 underline disabled:opacity-50">
          <Spinner loading={reissue.isPending}>Reissue the credential</Spinner>
        </button>
      ) : null}
    </div>
  );
}
